import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { accessApi } from "./api";
import {
  DISPLAY_NAME_MAX,
  DISPLAY_NAME_PROBLEM_KEY,
  PASSWORD_PROBLEM_KEY,
  displayNameProblem,
  passwordProblem,
} from "./codes";
import { readFailure } from "./failure";
import { PublicHeading } from "./public-layout";
import { readInviteLink } from "./routing";

/**
 * The first login from the invitation ([F-029], [AC-052], [DO-012]). A new
 * account has no password and never gets one from the administration: the
 * person sets it here, together with their display name, and the account
 * becomes active.
 *
 * The link is `/erste-anmeldung#<account>.<secret>`. The secret rides in the
 * fragment - no server, proxy log or Referer ever sees it - and is spent on
 * SUBMIT, not on opening: mailbox scanners open every link in a mail. The
 * fragment is read once and cleared from the address bar, so a screenshot or
 * a reload does not carry it. No session follows; the person logs in at the
 * mask afterwards.
 *
 * A display name that is taken is never answered with "taken" ([F-030]): the
 * server offers a free variation, which one click takes over.
 */
export function FirstLoginPage(): JSX.Element {
  const { t } = useTranslation();
  const [link] = useState(() => readInviteLink(window.location.hash));
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  const nameProblem = displayName.trim() === "" ? null : displayNameProblem(displayName);
  const pwProblem = password === "" ? null : passwordProblem(password, repeat === "" ? undefined : repeat);
  const ready =
    link !== null &&
    displayName.trim() !== "" &&
    nameProblem === null &&
    repeat !== "" &&
    passwordProblem(password, repeat) === null;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!link || !ready) return;
    setBusy(true);
    setError(null);
    setSuggestion(null);
    try {
      await accessApi.firstLogin({ ...link, password, display_name: displayName });
      setDone(true);
    } catch (failure) {
      const read = readFailure(failure, t("firstLogin.failed"));
      setError(read.message);
      setSuggestion(read.suggestion);
    } finally {
      setBusy(false);
    }
  };

  if (!link) {
    return (
      <section data-testid="first-login_page">
        <PublicHeading testid="first-login_title" title={t("firstLogin.title")} />
        <p data-testid="first-login_incomplete" role="alert" className="text-sm text-destructive-text">
          {t("firstLogin.incomplete")}
        </p>
        <Link to="/anmelden" data-testid="first-login_to-login" className="mt-6 block text-sm underline underline-offset-4">
          {t("firstLogin.toLogin")}
        </Link>
      </section>
    );
  }

  if (done) {
    return (
      <section data-testid="first-login_page">
        <PublicHeading testid="first-login_title" title={t("firstLogin.title")} />
        <p data-testid="first-login_done" role="status" className="text-sm">
          {t("firstLogin.done")}
        </p>
        <Button asChild className="mt-6 min-h-11 w-full">
          <Link to="/anmelden" data-testid="first-login_continue">
            {t("firstLogin.toLogin")}
          </Link>
        </Button>
      </section>
    );
  }

  return (
    <section data-testid="first-login_page">
      <PublicHeading testid="first-login_title" title={t("firstLogin.title")} lead={t("firstLogin.lead")} />
      <form data-testid="first-login_form" className="space-y-4" onSubmit={(event) => void submit(event)}>
        <div className="space-y-2">
          <Label htmlFor="first-login-name">{t("firstLogin.displayName")}</Label>
          <Input
            id="first-login-name"
            data-testid="first-login_display-name"
            autoFocus
            required
            maxLength={DISPLAY_NAME_MAX * 2}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t("firstLogin.displayNameHint")}</p>
          {nameProblem ? (
            <p data-testid="first-login_name-problem" className="text-xs text-destructive-text">
              {t(DISPLAY_NAME_PROBLEM_KEY[nameProblem])}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="first-login-password">{t("firstLogin.password")}</Label>
          <Input
            id="first-login-password"
            data-testid="first-login_password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t("forms.password.hint")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="first-login-repeat">{t("firstLogin.passwordRepeat")}</Label>
          <Input
            id="first-login-repeat"
            data-testid="first-login_password-repeat"
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
          {pwProblem ? (
            <p data-testid="first-login_password-problem" className="text-xs text-destructive-text">
              {t(PASSWORD_PROBLEM_KEY[pwProblem])}
            </p>
          ) : null}
        </div>
        <Button data-testid="first-login_submit" type="submit" className="min-h-11 w-full" disabled={busy || !ready}>
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {t("firstLogin.submit")}
        </Button>
      </form>

      {error ? (
        <div data-testid="first-login_error" role="alert" className="mt-4 space-y-2 text-sm text-destructive-text">
          <p>{error}</p>
          {suggestion ? (
            <Button
              data-testid="first-login_take-suggestion"
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setDisplayName(suggestion);
                setSuggestion(null);
                setError(null);
              }}
            >
              {t("forms.displayName.takeSuggestion", { name: suggestion })}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
